import { Image } from '@chakra-ui/react';
import type { ExplicitAddress } from '@cronos-app/sdk';
import { BONDING_TOKEN_DECIMALS } from '@cronos-app/sdk';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { TokenConfig } from '@ui/config/token';
import { Token } from '@ui/config/token';
import { useSDK } from '@ui/utils';

interface UseBondingTokenConfigOptions {
  tokenAddress: ExplicitAddress | undefined;
  enabled?: boolean;
}

export const useBondingTokenConfig = ({
  tokenAddress,
  enabled,
}: UseBondingTokenConfigOptions): UseQueryResult<TokenConfig> => {
  const sdk = useSDK();

  return useQuery({
    queryKey: ['useBondingTokenConfig', tokenAddress, sdk],
    queryFn: async () => {
      if (!tokenAddress) throw new Error('tokenAddress is empty');

      const bondingCurveModule = sdk.getBondingCurveModule();
      const tokenInfo = await bondingCurveModule.getTokenInfo(tokenAddress);

      const BondingTokenConfig: TokenConfig = {
        type: Token.Bonding,
        symbol: tokenInfo.data.ticker,
        address: tokenAddress,
        decimals: BONDING_TOKEN_DECIMALS,
        displayDecimals: 6,
        Icon: (props) => {
          return (
            <Image
              src={tokenInfo.image}
              alt={`${tokenInfo.data.name} icon`}
              {...props}
            />
          );
        },
      };
      return BondingTokenConfig;
    },
    // config will never change; avoid subscribers rerendering (Icon is new function every time)
    staleTime: Infinity,
    enabled: enabled === false ? false : !!tokenAddress && !!sdk,
  });
};
